import React, { Fragment } from "react";
import { Col, Container, Row } from "react-bootstrap";
import styled from "styled-components";
import Text from "./Text";
import { Check, NoCheck, FoundersIcon, ProIconGold } from "../../utility/icons";
import styles from "../../utility/styles";
import Button from './Button';

const Divider = styled.hr`
    color: ${({ color }) => color};
    height: ${({ height }) => height}px;
    margin: 0 41px 16px 41px;
    @media only screen and ${styles.breakpoints.mobileL} {
        margin: 0 9px 16px 9px;
    }
`;

const PlanCard = styled.div`
    position: relative;
    display: flex;
    flex: 1;
    min-width: 0;
    background: ${styles.colors.darkBackground};
    border: 1px solid ${({ $featured }) => ($featured ? 'var(--tz-gold-border, rgba(252, 206, 13, 0.45))' : 'var(--tz-hairline, rgba(255, 255, 255, 0.07))')};
    flex-direction: column;
    border-radius: 14px;
    padding: 24px;
    @media only screen and ${styles.breakpoints.tablet} {
        margin-bottom: 16px;
    }
`;

// Equal-height header region so the three CTAs sit on one baseline (desktop).
const PlanTop = styled.div``

const PlanCta = styled.div`
    margin-top: auto;
    padding-top: 24px;
    display: flex;
    flex-direction: column;
`

const PlanKickerRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
`;

const PriceHero = styled.div`
    font-family: ${styles.fonts.geist};
    font-size: 40px;
    line-height: 46px;
    font-weight: 600;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
    color: ${({ $gold }) => ($gold ? styles.colors.founders : styles.colors.textPrimary)};
    & small {
        font-size: 16px;
        font-weight: 500;
        letter-spacing: 0;
        color: ${styles.colors.lightGray};
        margin-left: 4px;
        white-space: nowrap;
    }
`;

const StyledRow = styled(Row)`
    margin: 0 41px 16px 41px;
    @media only screen and ${styles.breakpoints.mobileL} {
        margin: 0 -15px 16px -15px;
    }
`;

// ---------------------------------------------------------------------------
// Footnote system: feature strings carry trailing `*`/`**` markers and
// disclaimers arrive as newline-separated `*`-prefixed sentences. Rather than
// printing the raw ASCII, split the markers out and render them as styled
// superscripts tied to a footnote list.
// ---------------------------------------------------------------------------

const splitFootnote = text => {
    const match = (text || '').match(/^(.*?)(\*+)$/)
    return match ? { label: match[1].trim(), marker: match[2] } : { label: text, marker: '' }
}

const parseFootnoteLines = text => (text || '')
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 0)
    .map(line => {
        const match = line.match(/^(\*+)\s*(.*)$/)
        return match ? { marker: match[1], text: match[2] } : { marker: '', text: line }
    })

const FootnoteMark = styled.sup`
    color: ${({ color }) => color || styles.colors.founders};
    font-weight: 600;
    font-size: 0.75em;
    margin-left: 1px;
`

const FootnoteRow = styled.div`
    display: flex;
    align-items: baseline;
    gap: 6px;
    & + & {
        margin-top: 4px;
    }
`

const Footnotes = ({ text, color, markerColor, markedOnly = false }) => {
    // markedOnly drops marker-less preamble lines (e.g. the founders "contribution of
    // your choice" sentence, which the card itself already states).
    const lines = parseFootnoteLines(text).filter(l => !markedOnly || l.marker)
    if (!lines.length) return null
    return (
        <div style={{ marginTop: '12px' }}>
            {lines.map(({ marker, text: line }) => (
                <FootnoteRow key={line}>
                    {!!marker && <Text.Tiny variant='bold' color={markerColor || color}>{marker}</Text.Tiny>}
                    <Text.Tiny color={color}>{line}</Text.Tiny>
                </FootnoteRow>
            ))}
        </div>
    )
}

const ListCheck = ({ color }) => (
    <svg width='14' height='14' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg' style={{ flexShrink: 0, marginTop: '6px' }}>
        <path d='M4 12.5L9.5 18L20 6.5' stroke={color} strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
    </svg>
)

const ItemRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 10px;
`

// Key-feature strings arrive prefixed with "+ " (they render as raw text in the
// in-app upgrade menu); strip the prefix and lift trailing markers here instead.
const FeatureItem = ({ text, color, markerColor }) => {
    const { label, marker } = splitFootnote((text || '').replace(/^\+\s*/, ''))
    return (
        <ItemRow>
            <ListCheck color={color} />
            <Text.Body color={color}>
                {label}
                {!!marker && <FootnoteMark color={markerColor || color}>{marker}</FootnoteMark>}
            </Text.Body>
        </ItemRow>
    )
}

const MicroLabel = styled.span`
    font-family: ${styles.fonts.geistMono};
    font-size: 11px;
    line-height: 16px;
    font-weight: 500;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: ${({ color }) => color || styles.colors.lightGray};
`

const PlanChip = styled(MicroLabel)`
    border-radius: 6px;
    padding: 2px 8px;
    white-space: nowrap;
    background-color: ${styles.colors.founders};
    color: ${styles.colors.black};
`

const QuipCallout = styled.div`
    margin: 16px 0 0;
    padding: 12px 14px;
    border-radius: 10px;
    display: flex;
    align-items: flex-start;
    gap: 10px;
    background: ${styles.colors.founders};
    color: #1C2028;
    font-family: ${styles.fonts.geist};
    font-size: 13px;
    line-height: 18px;
    font-weight: 500;
    & b {
        font-weight: 700;
    }
    & svg {
        flex-shrink: 0;
        margin-top: 2px;
    }
    & span {
        text-wrap: balance;
    }
`

const QuipTagIcon = () => (
    <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
        <path d='M20.59 13.41L10.99 3.82A2 2 0 0 0 9.58 3.23H4a1 1 0 0 0-1 1v5.58c0 .53.21 1.04.59 1.42l9.59 9.59a2 2 0 0 0 2.82 0l4.59-4.59a2 2 0 0 0 0-2.82z' />
        <circle cx='7.5' cy='7.5' r='1' fill='currentColor' stroke='none' />
    </svg>
)

const FootnoteBlock = styled.div`
    max-width: 760px;
    margin: 20px auto 0;
    padding: 0 12px;
`

const Heading = () => {
    return (
        <StyledRow>
            <Col xs={8} className='d-flex align-items-center justify-content-center' />
            <Col xs={2} className='d-flex align-items-start justify-content-center'>
                <Text.Heading variant='bold' type='h5' color={styles.colors.dimHighlight}>Free</Text.Heading>
            </Col>
            <Col xs={2} className='d-flex align-items-center justify-content-center'>
                <Text.Heading variant='bold' type='h5' style={{ color: styles.colors.founders }}>Pro</Text.Heading>
            </Col>
        </StyledRow>
    )
}

// Default grouping used by the public marketing pages (Subscribe, App). Features are
// matched to a group by their `group` key (added in PagesController#serialize_pro_features).
// Consumers that pass ungrouped features (e.g. the in-app ProUpgradeMenu) fall back to a
// flat list, so this is fully backward compatible.
const DEFAULT_FEATURE_GROUPS = [
    { key: 'data', label: 'Unlimited data & history' },
    { key: 'insights', label: 'Insights & reports' },
    { key: 'drives', label: 'Plan & control your drives' },
    { key: 'fsd', label: 'FSD & Autopilot' },
]

const GroupHeading = ({ label }) => (
    <StyledRow style={{ marginBottom: '8px', marginTop: '40px' }}>
        <Col xs={12}>
            <Text.Heading type='h5' variant='bold' color={styles.colors.textPrimary}>
                {label}
            </Text.Heading>
        </Col>
    </StyledRow>
)

const renderRows = features => features.map((feature, index) => (
    <Body
        key={feature.text || index}
        freeText={feature.freeText}
        text={feature.text}
        footnotes
    />
))

const Grouped = ({ features = [], groups = DEFAULT_FEATURE_GROUPS }) => {
    const hasGroups = features.some(({ group }) => !!group)
    if (!hasGroups) return <>{renderRows(features)}</>

    const ungrouped = features.filter(({ group }) => !group)
    return (
        <>
            {groups.map(({ key, label }) => {
                const groupFeatures = features.filter(({ group }) => group === key)
                if (!groupFeatures.length) return null
                return (
                    <Fragment key={key}>
                        <GroupHeading label={label} />
                        {renderRows(groupFeatures)}
                    </Fragment>
                )
            })}
            {ungrouped.length > 0 && renderRows(ungrouped)}
        </>
    )
}

const Footer = ({ onClick, monthly, annual, founders, savings_percent, key_features = [], disclaimer, upgrade_features,...props }) => {
    const annualChip = savings_percent ? `Save ${savings_percent}%` : 'Best value'
    const freeFeatures = key_features.filter(({ is_free }) => is_free === true);
    const proFeatures = key_features.filter(({ is_free }) => is_free === false);
    const onClickInternal = e => {
        e.preventDefault();
        if (typeof onClick === 'function') {
            onClick();
        } else {
            window.location = '/subscribe#input';
        }
    }


    return (
        <>
            <div className='text-center' style={{ margin: '24px 0 48px' }}>
                <Text.Heading type='h3' variant='bold' color={styles.colors.dimHighlight}>Membership options built around your needs</Text.Heading>
                <Text.Body color={styles.colors.lightGray} style={{ marginTop: '8px' }}>Start free. Go Pro when you're ready.</Text.Body>
            </div>
            <Row>
                <Col xs={12} md={4} className='d-flex mt-3'>
                    <PlanCard>
                        <PlanTop>
                            <PlanKickerRow>
                                <MicroLabel>Basic</MicroLabel>
                            </PlanKickerRow>
                            <PriceHero>Free<small>(but awesome)</small></PriceHero>
                            <Text.Caption color={styles.colors.lightGray} style={{ marginTop: '4px' }}>Everything you need to start.</Text.Caption>
                        </PlanTop>
                        <div className='d-flex flex-column' style={{ gap: '8px', marginTop: '4px' }}>
                            {freeFeatures.slice(0, 3).map(({ text }) => (
                                <FeatureItem key={text} text={text} color={styles.colors.lightGray} markerColor={styles.colors.founders} />
                            ))}
                        </div>
                        <PlanCta>
                            <Button href='#download' as='a' style={{ backgroundColor: styles.colors.transparent, border: '1px solid var(--tz-hairline-strong, rgba(255, 255, 255, 0.14))', color: styles.colors.textPrimary }}>Get the App</Button>
                        </PlanCta>
                    </PlanCard>
                </Col>
                <Col xs={12} md={4} className='d-flex mt-3'>
                    <PlanCard>
                        <PlanTop>
                            <PlanKickerRow>
                                <MicroLabel color={styles.colors.founders}>TezLab Pro</MicroLabel>
                                <ProIconGold />
                            </PlanKickerRow>
                            <PriceHero $gold>{annual}<small>/ year</small></PriceHero>
                            <Text.Caption color={styles.colors.lightGray} style={{ marginTop: '4px' }}>{`or ${monthly}/month · ${annualChip} annually`}</Text.Caption>
                            <QuipCallout><QuipTagIcon /><span>That's <b>20% less</b> than our competition, and <b>100% better</b>.</span></QuipCallout>
                        </PlanTop>
                        <MicroLabel style={{ margin: '16px 0 8px 0' }}>Everything in Basic, plus</MicroLabel>
                        <div className='d-flex flex-column' style={{ gap: '8px' }}>
                            {proFeatures.slice(0, 3).map(({ text }) => (
                                <FeatureItem key={text} text={text} color={styles.colors.textPrimary} markerColor={styles.colors.founders} />
                            ))}
                        </div>
                        <Text.Link href='#features' font='caption' color={styles.colors.lightGray} style={{ marginTop: '12px' }}>Compare all features</Text.Link>
                        <PlanCta>
                            <Button onClick={onClickInternal} style={{ backgroundColor: styles.colors.founders, color: styles.colors.black }}>Go Pro</Button>
                        </PlanCta>
                    </PlanCard>
                </Col>
                {!!founders && (
                    <Col xs={12} md={4} className='d-flex mt-3'>
                        <PlanCard $featured>
                            <PlanTop>
                                <PlanKickerRow>
                                    <MicroLabel color={styles.colors.founders}>Founders Series</MicroLabel>
                                    <FoundersIcon />
                                </PlanKickerRow>
                                <PriceHero>{founders}</PriceHero>
                                <Text.Caption color={styles.colors.lightGray} style={{ marginTop: '4px' }}>Minimum contribution. Yours for life.</Text.Caption>
                            </PlanTop>
                            <MicroLabel style={{ margin: '16px 0 8px 0' }}>What Founders Series members get</MicroLabel>
                            <div className='d-flex flex-column' style={{ gap: '8px' }}>
                                <FeatureItem text='TezLab Pro for Life' color={styles.colors.textPrimary} markerColor={styles.colors.founders} />
                                <FeatureItem text='Tree Planted In Your Name' color={styles.colors.textPrimary} markerColor={styles.colors.founders} />
                                <FeatureItem text='In-App Founders Series Badge' color={styles.colors.textPrimary} markerColor={styles.colors.founders} />
                                <FeatureItem text='2 Vehicle Connections Included*' color={styles.colors.textPrimary} markerColor={styles.colors.founders} />
                            </div>
                            <Text.Link href='/founders' font='caption' color={styles.colors.lightGray} style={{ marginTop: '12px' }}>Learn more</Text.Link>
                            <PlanCta>
                                <Button onClick={onClickInternal} style={{ backgroundColor: styles.colors.transparent, border: '1px solid var(--tz-gold-border, rgba(252, 206, 13, 0.45))', color: styles.colors.founders }}>Join Founders Series</Button>
                            </PlanCta>
                        </PlanCard>
                    </Col>
                )}
            </Row>
            <FootnoteBlock>
                <Footnotes text={disclaimer} color={styles.colors.mediumGray} markerColor={styles.colors.founders} />
                {!!upgrade_features?.founders_disclaimer && (
                    <Footnotes markedOnly text={upgrade_features?.founders_disclaimer} color={styles.colors.mediumGray} markerColor={styles.colors.founders} />
                )}
            </FootnoteBlock>
        </>
    )
}

/**
 *
 * @param {{text:string, freeText:string, size:string, type:string, footnotes?:boolean}} props
 * @returns
 */
const Body = ({ text, freeText, isFree, highlight = false, footnotes = false, size = 'Heading', type = 'h6' }) => {
    const { label, marker } = footnotes ? splitFootnote(text) : { label: text, marker: '' }
    return (
        <>
            <Divider color={styles.colors.darkGray} height={1} />
            <StyledRow>
                <Col xs={8} className='d-flex align-items-center'>
                    {React.createElement(
                        Text[size],
                        {
                            type,
                            variant: highlight ? 'bold' : undefined,
                            color: highlight ? styles.colors.dimHighlight : styles.colors.lightGray,
                        },
                        marker ? <>{label}<FootnoteMark>{marker}</FootnoteMark></> : label
                    )}
                </Col>
                <Col xs={2} className='d-flex align-items-center justify-content-center'>
                    {freeText ? (
                        <Text.Tiny color={styles.colors.mediumGray}>{freeText}</Text.Tiny>
                    ) : (
                        <NoCheck />
                    )}
                </Col>
                <Col xs={2} className='d-flex align-items-center justify-content-center'>
                    <Check backgroundFill={styles.colors.background} fill={styles.colors.founders} />
                </Col>
            </StyledRow>
        </>
    )
}

export default {
    Heading,
    Footer,
    Body,
    GroupHeading,
    Grouped
}
